/**
 * Per-relay `since` bookkeeping for incremental reads.
 *
 * A single global `since` is wrong the moment two relays disagree — which is
 * always. Relay A is caught up to T, relay B is an hour behind; asking both for
 * `since = T` permanently loses everything B was going to send. So the watermark
 * is keyed by *(relay, filter fingerprint)* and each relay is asked for its own
 * gap.
 *
 * The only thing that may set a watermark is an event that relay delivered for
 * that filter. In particular the *store's* newest matching event is not a
 * watermark, and using it as one is a data-loss bug this module used to have. The
 * store is filled by capped page reads, so what it holds for a subject is a set
 * with holes in it, not a contiguous prefix. Say a profile's newest local note
 * arrived incidentally an hour ago while the notes behind it were never fetched:
 * `since = newest local` asks every relay for the empty window after it, the
 * relay honestly answers with nothing, and the hole is now permanent — backward
 * pagination windows below the *oldest* row held and can never reach a gap above
 * it. The screen shows a timeline that stops days ago and never moves again.
 * Asking with no `since` costs one bounded page and cannot do that.
 *
 * The watermark is also pulled back by an overlap window before use. Relay clocks
 * disagree by seconds to minutes, and `created_at` is author-supplied, so a
 * watermark used exactly is a watermark that drops events.
 */

import type { Filter, RelayBasedFilter, Timestamp } from "@setu/protocol";
import { normalizeRelayUrl } from "./normalize";

/**
 * Seconds to rewind the watermark by. 120s tolerates ordinary clock skew while
 * keeping the refetch small; the store dedups the overlap for free.
 */
export const DEFAULT_OVERLAP_SECONDS = 120;

/**
 * A stable identity for "this query", ignoring the parts that describe the
 * *window* rather than the *subject*.
 *
 * `since`, `until` and `limit` are excluded on purpose: a paginated read and an
 * incremental read of the same subject must share one watermark, or paginating
 * backwards would reset the forward watermark.
 */
export function filterFingerprint(filter: Filter): string {
  const keys = Object.keys(filter)
    .filter((key) => key !== "since" && key !== "until" && key !== "limit")
    .sort();
  const parts: string[] = [];
  for (const key of keys) {
    const value = (filter as Record<string, unknown>)[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      const sorted = [...(value as unknown[])].map(String).sort();
      parts.push(`${key}=${sorted.join(",")}`);
    } else {
      parts.push(`${key}=${String(value)}`);
    }
  }
  return parts.join("&");
}

/** Watermarks keyed by (relay, filter fingerprint). */
export class SinceTracker {
  private readonly watermarks = new Map<string, Timestamp>();

  constructor(
    private readonly overlapSeconds: number = DEFAULT_OVERLAP_SECONDS,
  ) {}

  /** Number of tracked (relay, filter) pairs. */
  get size(): number {
    return this.watermarks.size;
  }

  /** Records the newest `created_at` seen from `relay` for `filter`. */
  record(relay: string, filter: Filter, timestamp: Timestamp): void {
    const key = this.key(relay, filter);
    const current = this.watermarks.get(key);
    if (current === undefined || timestamp > current) {
      this.watermarks.set(key, timestamp);
    }
  }

  /** The raw watermark, with no overlap applied. */
  newest(relay: string, filter: Filter): Timestamp | undefined {
    return this.watermarks.get(this.key(relay, filter));
  }

  /**
   * The `since` to send this relay, or `undefined` when this relay has told us
   * nothing about this filter yet — in which case the caller must not add a
   * `since` at all, because a first read wants history.
   */
  since(relay: string, filter: Filter): Timestamp | undefined {
    const watermark = this.newest(relay, filter);
    if (watermark === undefined) return undefined;
    return Math.max(0, watermark - this.overlapSeconds);
  }

  /**
   * Rewrites one relay-bound filter's `since`, leaving everything else intact.
   *
   * Returns the input unchanged when no watermark exists, so a cold start still
   * asks for history — whatever the local store happens to hold.
   */
  applyTo(relayFilter: RelayBasedFilter): RelayBasedFilter {
    const since = this.since(relayFilter.relay, relayFilter.filter);
    if (since === undefined) return relayFilter;
    return {
      relay: relayFilter.relay,
      filter: { ...relayFilter.filter, since },
    };
  }

  /** Forgets every watermark. Call on account switch. */
  reset(): void {
    this.watermarks.clear();
  }

  private key(relay: string, filter: Filter): string {
    return `${normalizeRelayUrl(relay)}|${filterFingerprint(filter)}`;
  }
}
